import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Permission } from '@account-users/account/models/Permission';

import * as fromUsers from './users/users.reducer';
import * as fromUser from './user/user.reducer';
import * as fromUserRoles from './roles/roles.reducer';
import * as fromPermissions from './permissions/permissions.reducer';
import * as fromLicenses from './user-licenses/user-licenses.reducer';

const getPermissionsState = createFeatureSelector<
  fromPermissions.PermissionsState
>(fromPermissions.PERMISSIONS_FEATURE_KEY);

export const getUserPermissions = createSelector(
  getPermissionsState,
  (state) => state.userPermissions,
);

const getUserRolesState = createFeatureSelector<fromUserRoles.UserRolesState>(
  fromUserRoles.USER_ROLES_FEATURE_KEY,
);

export const getUserRoles = createSelector(
  getUserRolesState,
  (state) => state.userRoles,
);

export const hasPermission = (permission: Permission) =>
  createSelector(getUserPermissions, (permissions) =>
    permissions.includes(permission),
  );
const getUserLicensesState = createFeatureSelector<
  fromLicenses.UserLicensesState
>(fromLicenses.USER_LICENSES_FEATURE_KEY);

export const getAllUserLicenses = createSelector(
  getUserLicensesState,
  (state) => state.licenses,
);

export const getAllUserPermissions = createSelector(
  getUserLicensesState,
  (state) => state.permissions,
);

export const getUserLicenceLoading = createSelector(
  getUserLicensesState,
  (state) => state.isLoadingPermissions,
);

const getUsersState = createFeatureSelector<fromUsers.UserState>(
  fromUsers.USERS_FEATURE_KEY,
);

export const getAllUsers = createSelector(
  getUsersState,
  (state) => state.users,
);

export const getNumberOfUsers = createSelector(
  getUsersState,
  (state) => state.users.length,
);

export const getUserLoading = createSelector(
  getUsersState,
  (state) => state.isLoadingUsers,
);

export const getBulkEditSuccess = createSelector(
  getUsersState,
  (state) => state.bulkEditSuccess,
);

export const getBulkEditFailed = createSelector(
  getUsersState,
  (state) => state.bulkEditFailed,
);

export const getBulkEdit = createSelector(
  getUsersState,
  (state) => state.bulkEdit,
);

export const getBulkEditAccountUserCSV = createSelector(
  getBulkEditSuccess,
  getBulkEditFailed,
  (success, failed) => {
    const columnNames = [
      'Email',
      'Account (id)',
      'Licence code',
      'Permission code',
      'Status',
    ];
    const header = columnNames.join(',');

    let csv = header;
    csv += '\r\n';

    success.map((c) => {
      csv += [
        c.email,
        c.accountId,
        c.licenseCode,
        c.permissionCode,
        'Success',
      ].join(',');
      csv += '\r\n';
    });

    failed.map((c) => {
      csv += [
        c.email,
        c.accountId,
        c.licenseCode,
        c.permissionCode,
        'Failed',
      ].join(',');
      csv += '\r\n';
    });
    return {
      csv: csv,
    };
  },
);

export const getAccountUserCSV = createSelector(
  getUsersState,
  ({ exportAllAccountUsers }) => {
    return { csv: exportAllAccountUsers };
  },
);

export const getAccountUserBulkPermissionLoading = createSelector(
  getUsersState,
  (state) => state.bulkEditIsLoading,
);

export const getBulkEditFormState = createSelector(
  getUsersState,
  (state) => state.bulkEditFormState,
);

export const getSelectedUsers = createSelector(
  getUsersState,
  (state) => state.selectedUsers,
);

export const getSelectedUsersCount = createSelector(
  getUsersState,
  (state) => state.selectedUsers.length,
);

const getUserState = createFeatureSelector<fromUser.UserState>(
  fromUser.USER_FEATURE_KEY,
);

export const getUserInvitationLink = createSelector(
  getUserState,
  (state) => state.invitationLink,
);

export const getUserIsLoadingInvitationLink = createSelector(
  getUserState,
  (state) => state.isLoadingInvitationLink === fromUser.LoadingState.Loading,
);
